import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap,
  BookOpen,
  Users,
  Sparkles,
  Compass,
  ShieldCheck,
  Target,
  ArrowRight,
  Check,
  FileText,
  Send,
  Download,
  BookMarked,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const KEY_STAGES = [
  {
    code: "AGES 4–5",
    label: "Little Hearts",
    age: "Ages 4–5",
    titles: ["Stories of Kindness", "First Steps in Mindfulness", "My Friends and Me"],
    focus: "Picture-led stories, gentle routines, sharing & feelings.",
    accent: "#E07A5F",
    route: "/education/foundation-book",
  },
  {
    code: "AGES 5–7",
    label: "Tiny Steps",
    age: "Ages 5–7",
    titles: ["Big Hearts, Small Hands", "The Buddha's Tales", "Babasaheb as a Boy"],
    focus: "Simple narrative, classroom routines, kindness in action.",
    accent: "#CFA052",
    route: "/education/ks1-book",
  },
  {
    code: "AGES 7–11",
    label: "Junior Programme",
    age: "Ages 7–11",
    titles: ["Compassion in Action", "The Path of Dhamma", "A Constitution for Equals"],
    focus: "Inquiry-led learning, civic values, story → reflection.",
    accent: "#1B6EA1",
    route: "/education/ks2-book",
  },
  {
    code: "AGES 11–14",
    label: "Youth Programme",
    age: "Ages 11–14",
    titles: ["Ethics & Society", "Ambedkar: Justice & Reform", "Mindfulness & Modern Life"],
    focus: "Critical thinking, debate, multi-faith comparison.",
    accent: "#5B8C5A",
    route: "/education/ks3-book",
  },
  {
    code: "AGES 14–16",
    label: "Senior Programme",
    age: "Ages 14–16",
    titles: ["Buddhism & Citizenship", "Equality, Rights & Responsibility", "Source-based Inquiry"],
    focus: "Deeper inquiry, source analysis, philosophical writing.",
    accent: "#001B3A",
    route: "/education/ks4-book",
  },
];

const CHAPTER_STEPS = [
  { n: "1", t: "Learning Objective", d: "A clear, measurable outcome that pupils can recognise." },
  { n: "2", t: "Story / Case Study", d: "An age-appropriate Buddha-tale or moment from Babasaheb's life." },
  { n: "3", t: "Concept Explanation", d: "The core idea in plain English, scaled to the age group." },
  { n: "4", t: "Real-world Application", d: "How this concept shows up in everyday British life, schools and communities." },
  { n: "5", t: "Activity Section", d: "Group, pair or individual task. Visual-led for the youngest; inquiry-led from age 7+." },
  { n: "6", t: "Reflection Questions", d: "Open prompts for journals, discussion or homework." },
  { n: "7", t: "Teacher Notes", d: "Pedagogical guidance, differentiation tips, and links to wider values learning (Teacher Edition only)." },
];

const ALIGNMENT = [
  { icon: BookOpen, label: "Values-led learning" },
  { icon: Compass, label: "Multi-faith respectful" },
  { icon: Users, label: "Kindness & citizenship" },
  { icon: ShieldCheck, label: "Inclusive, classroom-friendly tone" },
];

export default function Education() {
  return (
    <>
      {/* HERO */}
      <section className="relative bg-navy text-pearl overflow-hidden" data-testid="education-hero">
        <div className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 30%, rgba(207,160,82,0.4), transparent 50%), radial-gradient(circle at 10% 90%, rgba(224,122,95,0.3), transparent 50%)",
          }}
        />
        <div className="relative section-container py-24">
          <p className="eyebrow text-gold">For UK Schools & Educators</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Ambedkarite Buddhist <span className="text-gold italic">Educational Series</span>, UK Edition
          </h1>
          <p className="mt-6 text-pearl/80 max-w-3xl text-lg leading-relaxed">
            A values-based programme of stories, lessons and reflections drawn from the teachings of
            Gautama Buddha and the philosophy of Dr. B.R. Ambedkar, written in plain English for
            British classrooms and family living rooms, for learners from ages 4 through 16.
          </p>
          <p className="mt-6 text-gold font-display italic text-2xl">
            Education · Equality · Enlightenment
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/education/ks2-book" data-testid="edu-ks2-book-cta" className="px-7 py-3.5 bg-gold text-navy hover:bg-gold-light rounded-full font-medium inline-flex items-center gap-2">
              <BookMarked size={18} /> Read the Junior Programme book
            </Link>
            <Link to="/education/sample" data-testid="edu-sample-cta" className="px-7 py-3.5 border border-pearl/30 text-pearl hover:bg-pearl/10 rounded-full inline-flex items-center gap-2">
              <FileText size={18} /> Just the sample chapter
            </Link>
            <a href="/api/education/pilot-pack" data-testid="edu-pilot-pack-cta" className="px-7 py-3.5 border border-pearl/30 text-pearl hover:bg-pearl/10 rounded-full inline-flex items-center gap-2">
              <Download size={18} /> Download school pilot pack
            </a>
          </div>
        </div>
      </section>

      {/* KS2 STUDENT BOOK, NOW LIVE */}
      <section className="py-20 bg-white border-y border-navy/10" data-testid="education-ks2-feature">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute inset-0 bg-gold/15 rounded-2xl rotate-2" />
              <div className="relative bg-navy text-pearl rounded-2xl p-8 sm:p-10 border border-gold/40">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Phase 1 · Now reading</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
                  Junior Programme, twelve chapters, ready for the classroom.
                </h3>
                <p className="mt-5 text-pearl/80 text-sm leading-relaxed">
                  Across six small units, pupils meet Bhimrao the brave reader, the Buddha asking
                  his first honest questions, Savitribai walking with two saris, the Monkey King
                  who became a bridge, and the kind Emperor Ashoka. Each chapter follows the
                  standard 7-step rhythm and is built for British classrooms.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  <div className="bg-pearl/10 border border-pearl/15 rounded-md p-3">
                    <p className="font-display text-3xl text-gold">12</p>
                    <p className="text-[10px] uppercase tracking-widest text-pearl/70 mt-1">Chapters</p>
                  </div>
                  <div className="bg-pearl/10 border border-pearl/15 rounded-md p-3">
                    <p className="font-display text-3xl text-gold">6</p>
                    <p className="text-[10px] uppercase tracking-widest text-pearl/70 mt-1">Units</p>
                  </div>
                  <div className="bg-pearl/10 border border-pearl/15 rounded-md p-3">
                    <p className="font-display text-3xl text-gold">60'</p>
                    <p className="text-[10px] uppercase tracking-widest text-pearl/70 mt-1">Per lesson</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="eyebrow">New · Phase 1 prototype</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              The Junior Programme is <span className="italic text-saffron">live</span>.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Read the entire book online &mdash; from the cover and table of contents through to the
              final reflection. It includes a sticky chapter index for navigation and a clean
              print stylesheet so any chapter (or the whole book) can be saved as a PDF in one click.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Ready for the classroom &mdash; story, concept, real-world application, activity, reflection in every chapter.</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Browser-print stylesheet for A4 / Letter paper, including a printable cover.</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Multi-faith respectful, inclusive, and rooted in values of dignity and fairness.</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/education/ks2-book"
                data-testid="ks2-feature-cta"
                className="inline-flex items-center gap-2 px-7 py-3 bg-navy text-pearl hover:bg-navy-light rounded-full font-medium"
              >
                <BookMarked size={18} /> Open the book
              </Link>
              <Link
                to="/education/ks2-workbook"
                data-testid="ks2-workbook-feature-cta"
                className="inline-flex items-center gap-2 px-7 py-3 border border-navy/20 text-navy hover:bg-navy/5 rounded-full font-medium"
              >
                <BookMarked size={18} /> Workbook (NEW)
              </Link>
              <Link
                to="/education/ks2-teacher-guide"
                data-testid="ks2-teacher-feature-cta"
                className="inline-flex items-center gap-2 px-7 py-3 border border-navy/20 text-navy hover:bg-navy/5 rounded-full font-medium"
              >
                <BookMarked size={18} /> Teacher Guide (NEW)
              </Link>
              <a
                href="/api/education/pilot-pack"
                data-testid="pilot-pack-feature-cta"
                className="inline-flex items-center gap-2 px-7 py-3 border border-navy/20 text-navy hover:bg-navy/5 rounded-full font-medium"
              >
                <Download size={18} /> School pilot pack (.zip)
              </a>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              The complete KS2 resource set: <strong>Student Book</strong> + <strong>Workbook</strong> (12 worksheets) + <strong>Teacher Guide</strong> (12 lesson plans). Starter previews are also live for <Link to="/education/foundation-book" className="underline text-saffron" data-testid="foundation-inline-link">Foundation (Ages 4–5)</Link>, <Link to="/education/ks1-book" className="underline text-saffron" data-testid="ks1-inline-link">KS1 (5–7)</Link>, <Link to="/education/ks3-book" className="underline text-saffron" data-testid="ks3-inline-link">KS3 (11–14)</Link> and <Link to="/education/ks4-book" className="underline text-saffron" data-testid="ks4-inline-link">KS4 (14–16)</Link>. Pilot pack: 4 PDFs, ~80 KB.
            </p>
          </div>
        </div>
      </section>

      {/* WHY THIS SERIES */}
      <section className="py-20 bg-pearl" data-testid="education-why">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our ethos</p>
            <h2 className="mt-3 font-display text-4xl text-navy">A non-religious, values-led classroom resource.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed">
            <p>
              This is <strong>not a religious book series</strong>. It is an inclusive,
              non-proselytising programme that draws ethical and civic insights from two
              extraordinary Indian intellectual traditions: the Buddha's teachings on mindfulness,
              compassion and non-violence, and Babasaheb Dr. B.R. Ambedkar's lifelong work on
              equality, dignity and social justice.
            </p>
            <p>
              The series is written for British classrooms and family living rooms &mdash; from ages 4
              to 16 &mdash; in plain English. Every chapter connects timeless ideas to real, everyday
              British life: friendship, fairness, kindness, multi-faith communities, and the
              quiet civic responsibilities we all share.
            </p>
          </div>
        </div>
      </section>

      {/* ALIGNMENT */}
      <section className="py-16 bg-white border-y border-navy/10" data-testid="education-alignment">
        <div className="section-container">
          <p className="eyebrow text-center">Curriculum alignment</p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {ALIGNMENT.map((a) => {
              const I = a.icon;
              return (
                <div key={a.label} className="bg-pearl border border-navy/10 rounded-2xl p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-saffron/15 text-saffron mb-3">
                    <I size={20} strokeWidth={1.6} />
                  </div>
                  <p className="text-sm text-navy font-medium leading-tight">{a.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY STAGES */}
      <section className="py-20 bg-pearl" data-testid="education-stages">
        <div className="section-container">
          <p className="eyebrow">The full series</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy max-w-3xl">Five age groups, from Little Hearts to the Senior Programme.</h2>
          <p className="mt-4 text-slate-700 max-w-3xl">
            Each age group includes a Student Book, a Workbook and a Teacher Guide. Together: 15 titles
            forming a complete progression from picture-led stories at age 4 to source-based inquiry at age 16.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {KEY_STAGES.map((s, i) => (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-white border border-navy/10 rounded-2xl overflow-hidden flex flex-col"
                data-testid={`stage-${s.code.toLowerCase()}`}
              >
                <div className="h-2" style={{ backgroundColor: s.accent }} />
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: s.accent }}>{s.code}</span>
                  <h3 className="mt-2 font-display text-xl text-navy leading-tight">{s.label}</h3>
                  <p className="text-xs text-slate-500 mt-1">{s.age}</p>
                  <p className="mt-4 text-sm text-slate-700 leading-relaxed">{s.focus}</p>
                  <div className="mt-4 pt-4 border-t border-navy/5">
                    <p className="text-[10px] uppercase tracking-widest text-navy/50 mb-2">Working titles</p>
                    <ul className="text-xs text-slate-700 space-y-1">
                      {s.titles.map((t) => (
                        <li key={t} className="flex items-start gap-1.5">
                          <Check size={12} className="text-gold mt-0.5 shrink-0" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-4 pt-4 border-t border-navy/5 flex flex-wrap gap-1.5 text-[10px]">
                    <span className="px-2 py-0.5 rounded-sm bg-navy/5 text-navy">Student Book</span>
                    <span className="px-2 py-0.5 rounded-sm bg-navy/5 text-navy">Workbook</span>
                    <span className="px-2 py-0.5 rounded-sm bg-navy/5 text-navy">Teacher Guide</span>
                  </div>
                  {s.route && (
                    <Link
                      to={s.route}
                      data-testid={`stage-cta-${s.code.toLowerCase().replace(/\s/g, "-")}`}
                      className="mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-pearl hover:opacity-90"
                      style={{ backgroundColor: s.accent }}
                    >
                      Read preview <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPTER STRUCTURE */}
      <section className="py-20 bg-white" data-testid="education-chapter">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Inside every chapter</p>
            <h2 className="mt-3 font-display text-4xl text-navy">Seven steps. Every lesson. Every age.</h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              A standardised teaching structure means teachers know exactly what to expect from any chapter,
              at any level, and learners build confidence through familiar rhythm.
            </p>
            <Link to="/education/sample" className="mt-6 inline-flex items-center gap-2 text-saffron hover:text-saffron-light font-medium" data-testid="chapter-sample-link">
              See it in action, Junior Programme sample chapter <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-3">
              {CHAPTER_STEPS.map((c) => (
                <li key={c.n} className="bg-pearl border border-navy/10 rounded-xl p-5 flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navy text-pearl flex items-center justify-center font-display text-lg">
                    {c.n}
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-navy">{c.t}</h3>
                    <p className="text-sm text-slate-700 mt-1">{c.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* PEDAGOGY & COMPLIANCE */}
      <section className="py-20 bg-pearl" data-testid="education-pedagogy">
        <div className="section-container grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-navy/10 rounded-2xl p-8">
            <Target className="text-gold" size={28} strokeWidth={1.5} />
            <h3 className="mt-4 font-display text-2xl text-navy">Teaching principles</h3>
            <ul className="mt-4 space-y-2.5 text-slate-700 text-sm">
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" />Age-appropriate language scaling across all groups</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" />Visual-heavy content for the youngest learners</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" />Inquiry-based learning from age 7 onwards</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" />Critical thinking & structured discussion at age 11+</li>
              <li className="flex gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" />Source-based reflective writing for the Senior Programme</li>
            </ul>
          </div>
          <div className="bg-white border border-navy/10 rounded-2xl p-8">
            <ShieldCheck className="text-saffron" size={28} strokeWidth={1.5} />
            <h3 className="mt-4 font-display text-2xl text-navy">Values & inclusion</h3>
            <ul className="mt-4 space-y-2.5 text-slate-700 text-sm">
              <li className="flex gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" />Multi-faith respect throughout</li>
              <li className="flex gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" />Anchored in dignity, fairness, kindness and mutual respect</li>
              <li className="flex gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" />Calm, classroom-friendly tone</li>
              <li className="flex gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" />No proselytising and no political bias</li>
              <li className="flex gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" />Inclusive illustrations that reflect diverse families</li>
            </ul>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 bg-white" data-testid="education-timeline">
        <div className="section-container">
          <p className="eyebrow">The build plan</p>
          <h2 className="mt-3 font-display text-4xl text-navy max-w-3xl">A staged production from sample to full series.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: "Phase 1", t: "Prototype", w: "3–4 weeks", body: "1 sample unit per age group + 1 full Teacher Guide sample + visual design prototype." },
              { n: "Phase 2", t: "Full content", w: "8–12 weeks", body: "All 15 books written by qualified educators with editorial review." },
              { n: "Phase 3", t: "Design & launch", w: "4 weeks", body: "Print-ready PDFs, editable source files, and a digital-friendly version for the ABGN-UK platform." },
            ].map((p) => (
              <div key={p.n} className="bg-pearl border border-navy/10 rounded-2xl p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-saffron">{p.n}</p>
                <h3 className="mt-2 font-display text-2xl text-navy">{p.t}</h3>
                <p className="text-xs text-slate-500 mt-1">{p.w}</p>
                <p className="mt-4 text-sm text-slate-700 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTEREST FORM */}
      <section id="interest" className="py-24 bg-navy text-pearl scroll-mt-24" data-testid="education-interest">
        <div className="section-container max-w-3xl">
          <p className="eyebrow text-gold">Express interest</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl">Get involved early.</h2>
          <p className="mt-6 text-pearl/80 leading-relaxed">
            Are you a teacher, headteacher, education lead, parent, funder or community educator
            interested in piloting this series in your school or community group? Leave us a short
            note and we'll be in touch with sample materials and updates.
          </p>
          <InterestForm />
        </div>
      </section>
    </>
  );
}

function InterestForm() {
  const [form, setForm] = useState({ name: "", email: "", role: "Teacher", school: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: `Education Series · ${form.role}${form.school ? " · " + form.school : ""}`,
        message: form.message || "(no additional message)",
      });
      toast.success("Thank you, we'll be in touch.");
      setSent(true);
      setForm({ name: "", email: "", role: "Teacher", school: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 bg-pearl text-navy border border-pearl/20 p-8 sm:p-10 rounded-2xl" data-testid="education-interest-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="ei-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="ei-email" />
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Role</span>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="ei-role"
            className="mt-2 w-full border border-navy/15 bg-white px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
          >
            <option>Teacher</option>
            <option>Headteacher</option>
            <option>Education Lead</option>
            <option>Pastoral Lead</option>
            <option>Parent</option>
            <option>Funder / Trust</option>
            <option>Community Educator</option>
            <option>Other</option>
          </select>
        </label>
        <Field label="School / Organisation (optional)" value={form.school} onChange={(v) => setForm({ ...form, school: v })} testid="ei-school" />
      </div>
      <label className="block mt-5">
        <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Message (optional)</span>
        <textarea
          rows={5}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Tell us about your school, year groups, or specific interest…"
          data-testid="ei-message"
          className="mt-2 w-full border border-navy/15 bg-white px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
        />
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="ei-submit"
        className="mt-6 inline-flex items-center gap-2 px-7 py-3 bg-navy text-pearl hover:bg-navy-light rounded-sm font-medium disabled:opacity-60"
      >
        <Send size={16} /> {loading ? "Sending…" : sent ? "Sent, thank you" : "Submit interest"}
      </button>
      <p className="mt-4 text-xs text-slate-500">
        Submissions are stored securely and reviewed by ABGN-UK educators. We will only contact you about
        the Educational Series.
      </p>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full border border-navy/15 bg-white px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
      />
    </label>
  );
}
